import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Carousel, Slide } from '@uhg-abyss/web/ui/Carousel';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { Text } from '@uhg-abyss/web/ui/Text';
import find from 'lodash/find';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { VirtualCareProviderCard } from '../../frontends/ProviderSearch/routes/MapView/VirtualCareProvidersBanner/VirtualCareProviderCard';
import {
  allVirtualCareSlideStyle,
  primarySlideStyle,
  viewAllCardCssOnMedical,
  viewAllLinkCss,
} from '../../frontends/ProviderSearch/routes/MapView/VirtualCareProvidersBanner/VirtualCareProvidersBanner.styled';
import { VirtualCareProviderModal } from '../../frontends/ProviderSearch/routes/MapView/VirtualCareProvidersBanner/VirtualCareProvidersModal';
import { useLagoon } from '../../hooks/useLagoon';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ConstantsRoutes } from '../ConstantsRoutes';
import {
  cardSubHeaderWithNavigatorStyle,
  mobileOnly,
  notMobile,
} from '../ConstantsStyles';
import { ContentWrapper } from '../ContentWrapper';
import { ContainerForAllSectionsStyled } from '../ProviderDetailTabs/ProviderDetailTabs.styled';
import { carouselStyle } from '../SnackCard/SnackCard.styles';
import { convertObjectToUrlParams } from '../Utils';
import { VirtualCareCostEstimateLink } from '../VirtualCare/common/VirtualCareCostEstimateLink';
import { VirtualSpecialtyCareCostInfo } from '../VirtualCare/common/VirtualSpecialtyCareCostInfo';
import { NoResultsFoundContainer } from './ResultSection.style';

type VirtualCareProvider = {
  serviceType: string;
  providerName: string;
  logo: string;
  link: string;
  linkType: string;
};
type VirtualCareProviders = {
  providers: VirtualCareProvider[];
  serviceType?: string;
};
interface PrimaryCareVirtualProps {
  virtualCareProviders: VirtualCareProviders;
}
export const PrimaryCareVirtual = ({
  virtualCareProviders,
}: PrimaryCareVirtualProps) => {
  const { t } = useTranslation();
  const mobileScreen = useMediaQuery(mobileOnly);
  const [isOpen, setIsOpen] = useState(false);
  const data = useLagoon('ui-messaging')();
  const allConfigs = useLagoon('config')();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProviderName, setSelectedProviderName] = useState<
    string | null
  >(null);
  const virtualPrimaryCareinfo = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE.VIRTUAL_PRIMARY_CARE_INFO,
  });
  const heading = virtualPrimaryCareinfo?.title;
  const headingInfo = virtualPrimaryCareinfo?.message;
  const costEstimate = find(data, {
    key: ConstantsLagoon.VIRTUAL_CARE.VIRTUAL_PRIMARY_CARE_COST_ESTIMATE,
  });
  const viewAll = t('SUGGESTED_PROVIDERS_CARD.VIEW_ALL');
  const mediumScreen = useMediaQuery('(max-width: 1025px)');
  const desktopScreen = useMediaQuery(notMobile);
  const { navigate } = useRouter();
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const showCostEstimateLink = allConfigs?.find(
    (row) => row.key === 'SHOW_COST_ESTIMATE_LINK_PRIMARY_CARE'
  )?.value;
  const handleViewMore = () => {
    const urlParams = convertObjectToUrlParams(chipStore, {
      chipValue: 'Primary Care',
      coverageType: 'M',
      locale: 'en-US',
      landedOnDetailsPageFromSearch: 'true',
      search: heading,
      'psx-source': 'primary-care-providers',
      fromLandingPage: 'true',
      showAllVirtualCareList: 'true',
    });
    navigate(`${ConstantsRoutes.PROVIDER_SEARCH_RESULTS.path}${urlParams}`);
  };
  const virtualCareProviderCount = virtualCareProviders?.providers?.length || 0;
  const slides = virtualCareProviders?.providers
    ?.slice(0, 3)
    .map((provider) => (
      <Slide css={primarySlideStyle} key="slide">
        <VirtualCareProviderCard
          key={provider?.providerName}
          mapView={false}
          mobileScreen={mobileScreen}
          primaryCareVirtual={true}
          provider={provider}
          serviceType={virtualCareProviders?.serviceType || ''}
          setIsModalOpen={setIsModalOpen}
          setSelectedProviderName={setSelectedProviderName}
        />
      </Slide>
    ));
  if (desktopScreen && virtualCareProviderCount > 3) {
    slides.push(
      <Slide
        css={{ ...primarySlideStyle, ...allVirtualCareSlideStyle }}
        key="all-virtual-care"
      >
        <Card
          css={viewAllCardCssOnMedical(mobileScreen)}
          key="all-virtual-care-link"
        >
          <Link
            afterIcon="chevron_right"
            css={viewAllLinkCss}
            data-auto-testid={`primarycare-virtual-care-view-all-link-text`}
            data-testid={`primarycare-virtual-care-view-all-link-text`}
            onClick={() => handleViewMore()}
            style={{
              minHeight: '56px',
            }}
          >
            <span
              style={{
                width: '130px',
                textAlign: 'left',
                paddingRight: '11%',
              }}
            >
              {t('VIRTUAL_CARE_PROVIDERS_BANNER.ALL_VIRTUAL_CARE')}
            </span>
          </Link>
        </Card>
      </Slide>
    );
  }
  const getSlides = () =>
    slides?.map((slide, index) => (
      <Flex
        css={{
          flex: '0 0 auto',
          marginBottom: '8px',
          width: '310px',
          height: '83px',
        }}
        key={`slide-${index}`}
      >
        {slide}
      </Flex>
    ));
  const getVirtualSpecialtyCareProviders = () =>
    virtualCareProviders ? (
      <Flex
        css={{
          width: '100%',
          gap: '16px',
          flexWrap: 'nowrap',
          backgroundColor: '#FFFFFF',
        }}
        direction="row"
      >
        {getSlides()}
      </Flex>
    ) : (
      <NoResultsFoundContainer>
        <Text>{Constants.No_RESULT_FOUND.N0_RESULT_FOUND_CONTENT}</Text>
      </NoResultsFoundContainer>
    );
  return (
    <ContainerForAllSectionsStyled
      data-auto-testid={`primarycare-virtual-care-section`}
      data-testid={`primarycare-virtual-care-section-`}
    >
      <ContentWrapper>
        <Flex
          css={{
            'abyss-flex-root': {
              gap: '48px',
              alignItems: 'center',
              flexWrap: 'wrap',
              '@media (max-width: 744px)': {
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              },
              '@media (max-width: 464px)': {
                gap: '5px',
                flexWrap: 'wrap',
                alignItems: 'center',
              },
            },
          }}
          justify="start"
        >
          <Flex
            css={{
              gap: '4px',
              alignItems: 'center',
              marginBottom: mobileScreen ? '16px' : '24px',
            }}
            direction="row"
          >
            <Heading
              css={cardSubHeaderWithNavigatorStyle}
              data-auto-testid="primary-care-virtual-care-heading"
              data-testid="primary-care-virtual-care-heading-text"
              level={2}
              size="sm"
              style={{ paddingTop: 0 }}
            >
              {heading}
            </Heading>
            <Popover
              content={headingInfo}
              css={{
                'abyss-popover-root': {
                  zIndex: '9',
                },
              }}
              onOpenChange={() => setIsOpen(!isOpen)}
              open={isOpen}
              title={heading}
            >
              <IconSymbol
                css={{ marginTop: mobileScreen ? '2px' : '4px' }}
                icon="info"
                onClick={() => setIsOpen(true)}
                size={16}
                variant="outlined"
              />
            </Popover>
          </Flex>
          {mobileScreen && virtualCareProviderCount > 3 && (
            <Link
              css={viewAllLinkCss}
              data-auto-testid={`primarycare-virtual-care-view-all-link-text`}
              data-testid={`primarycare-virtual-care-view-all-link-text`}
              fontWeight="bold"
              onClick={() => handleViewMore()}
              size="$md"
            >
              {viewAll} ({virtualCareProviderCount})
            </Link>
          )}
          <Flex
            css={{
              gap: '16px',
              alignItems: 'center',
              marginBottom: mobileScreen ? '16px' : '24px',
            }}
            direction="row"
          >
            {showCostEstimateLink === 'Y' ? (
              <VirtualCareCostEstimateLink
                additionalStyles={{}}
                adobeLinkTrackLocation="virtual primary care"
                costEstimate={costEstimate}
                testId="virtual-primary-care-cost-link"
              />
            ) : (
              <VirtualSpecialtyCareCostInfo
                additionalStyles={{}}
                isLandingPage={false}
              />
            )}
          </Flex>
        </Flex>
        {(mobileScreen || mediumScreen) && (
          <Carousel
            css={carouselStyle}
            data-auto-testid="virtual-specialty-care-carousel"
            data-testid="virtual-specialty-care-carousel"
            label=""
            slides={getSlides()}
            slidesPerView={1}
          />
        )}
        <VirtualCareProviderModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          selectedProviderName={selectedProviderName}
          virtualCareProviders={virtualCareProviders}
        />
        {desktopScreen && !mediumScreen && getVirtualSpecialtyCareProviders()}
      </ContentWrapper>
    </ContainerForAllSectionsStyled>
  );
};
